<script>
  import { createEventDispatcher, tick } from "svelte";
  import { useUrlParams } from "@stores/urlStore.js";
  import { Button } from "paxray-components-svelte";
  import NumberRuleModal from "./NumberRuleModal.svelte";
  import SliceElement from "./SliceElement.svelte";
  export let readElement = "";
  export let anontype = "";
  export let removeAnonType = "";
  export let firstElement = false;
  export let anontypeOptionsMap = {};
  export let hideLastChar = false;
  export let selected = false;
  export let multiSelected = false;
  export let selectedElementValues = [];
  export let appIcon = "";
  export let appTitle = "";
  export let onSelection = null;
  const dispatch = createEventDispatcher();
  let open = false;
  let elementRef;
  let optionsListEl;
  let menuPosition = { left: "0px", top: "0px", maxHeight: "none" };
  let menuWidth = "auto";
  let columnsWrapperEl;
  let numberAssistantOpen = false;
  let numberAssistantRule = {};
  let sliceElementOpen = false;
  let appIconFailed = false;
  const urlParams = useUrlParams();
  $: ({ idproject } = $urlParams);

  $: displayReadElement = hideLastChar
    ? readElement.replace(/[\s_]$/, "")
    : readElement;
  $: displayAnonType = hideLastChar ? anontype.replace(/[\s_]$/, "") : anontype;
  $: displayRemoveAnonType = hideLastChar
    ? removeAnonType.replace(/[\s_]$/, "")
    : removeAnonType;

  $: allMapOptions = Object.values(anontypeOptionsMap).flatMap(
    (col) => col.options || [],
  );

  $: isValidSelection =
    allMapOptions.some((o) => o.value === anontype) ||
    anontype !== removeAnonType;

  function isValidSelectedElement(selectedElement) {
    return (
      allMapOptions.some((o) => o.value === selectedElement.type) ||
      selectedElement.type !== selectedElement.removeAnonType
    );
  }

  $: if (open) positionMenu();

  async function focusSelectedOption() {
    await tick();
    const selectedOption = optionsListEl?.querySelector(
      '[aria-selected="true"], [data-remove-option="true"]',
    );
    selectedOption?.focus();
  }

  async function positionMenu() {
    await tick();
    if (!elementRef || !optionsListEl) return;

    const elementRect = elementRef.getBoundingClientRect();
    const columnsRect = columnsWrapperEl?.getBoundingClientRect();
    const nextMenuWidth = columnsRect
      ? `${columnsRect.width + 16}px`
      : "auto";
    if (nextMenuWidth !== menuWidth) {
      menuWidth = nextMenuWidth;
      await tick();
    }

    const gap = 4;
    const viewportPadding = 8;
    const obstructionBottom = [...document.querySelectorAll(
      ".header_wrapper, .control-toolbar",
    )].reduce((bottom, element) => {
      const rect = element.getBoundingClientRect();
      return rect.bottom <= elementRect.top ? Math.max(bottom, rect.bottom) : bottom;
    }, viewportPadding);
    const minTop = Math.max(viewportPadding, obstructionBottom + gap);
    // Opens below the word like a dropdown, above it only when there is more room,
    // so the rest of the row with its check buttons stays visible.
    const spaceBelow = window.innerHeight - elementRect.bottom - gap - viewportPadding;
    const spaceAbove = elementRect.top - gap - minTop;
    menuPosition = { ...menuPosition, maxHeight: "none" };
    await tick();
    const naturalHeight = optionsListEl.getBoundingClientRect().height;
    const openBelow = spaceBelow >= naturalHeight || spaceBelow >= spaceAbove;
    const maxHeight = Math.max(10 * 16, openBelow ? spaceBelow : spaceAbove);
    menuPosition = { ...menuPosition, maxHeight: `${maxHeight}px` };
    await tick();

    const positionedMenuRect = optionsListEl.getBoundingClientRect();
    const maxLeft = Math.max(
      viewportPadding,
      window.innerWidth - positionedMenuRect.width - viewportPadding,
    );
    const preferredTop = openBelow
      ? elementRect.bottom + gap
      : elementRect.top - positionedMenuRect.height - gap;
    const maxTop = Math.max(
      minTop,
      window.innerHeight - positionedMenuRect.height - viewportPadding,
    );

    menuPosition = {
      left: `${Math.min(Math.max(elementRect.left, viewportPadding), maxLeft)}px`,
      top: `${Math.min(Math.max(preferredTop, minTop), maxTop)}px`,
      maxHeight: `${maxHeight}px`,
    };
  }

  function selectOption(option) {
    const oldValue = anontype;
    anontype = option.value;
    open = false;

    if (oldValue !== anontype) {
      dispatch("typeChanged", { oldValue });
    }
  }

  function handleMenuMouseDown(event) {
    onSelection?.(event);
  }

  function handleMenuClick() {
    open = !open;
    if (open) focusSelectedOption();
  }

  function handleAppIconLoad(event) {
    try {
      const image = event.currentTarget;
      const canvas = document.createElement("canvas");
      canvas.width = 24;
      canvas.height = 24;
      const context = canvas.getContext("2d");
      context.drawImage(image, 0, 0, 24, 24);
      const data = context.getImageData(0, 0, 24, 24).data;
      let coloredPixels = 0;
      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        const spread =
          Math.max(data[i], data[i + 1], data[i + 2]) -
          Math.min(data[i], data[i + 1], data[i + 2]);
        if (alpha > 32 && spread > 18) coloredPixels += 1;
      }
      if (coloredPixels < 12) appIconFailed = true;
    } catch {
      // Keep the icon visible when the browser blocks canvas inspection.
    }
  }

  function handleOptionKeydown(event, option) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectOption(option);
      return;
    }
    if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(event.key)) return;

    const currentColumn = event.currentTarget.closest(".column");
    const columns = [...optionsListEl.querySelectorAll(".columns-wrapper .column")];
    if (currentColumn && columns.includes(currentColumn)) {
      const columnIndex = columns.indexOf(currentColumn);
      const columnOptions = [...currentColumn.querySelectorAll('[role="option"]')];
      const rowIndex = columnOptions.indexOf(event.currentTarget);
      let target;

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        const nextRow = Math.max(
          0,
          Math.min(
            columnOptions.length - 1,
            rowIndex + (event.key === "ArrowDown" ? 1 : -1),
          ),
        );
        target = columnOptions[nextRow];
      } else {
        const nextColumn = Math.max(
          0,
          Math.min(
            columns.length - 1,
            columnIndex + (event.key === "ArrowRight" ? 1 : -1),
          ),
        );
        const targetOptions = [...columns[nextColumn].querySelectorAll('[role="option"]')];
        target = targetOptions[Math.min(rowIndex, targetOptions.length - 1)];
      }

      event.preventDefault();
      target?.focus();
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") return;
    const options = [...optionsListEl.querySelectorAll('[role="option"], [role="button"]')];
    const currentIndex = options.indexOf(event.currentTarget);
    const nextIndex = Math.max(
      0,
      Math.min(
        options.length - 1,
        currentIndex + (event.key === "ArrowDown" ? 1 : -1),
      ),
    );
    event.preventDefault();
    options[nextIndex]?.focus();
  }

  function handleKey(event) {
    if (event.key === "Enter" || event.key === " ") {
      open = !open;
      event.preventDefault();
    } else if (event.key === "Escape") {
      open = false;
    }
  }

  function handleClickOutside(event) {
    if (
      (open || sliceElementOpen) &&
      elementRef &&
      document.body.contains(event.target) &&
      !elementRef.contains(event.target) &&
      !optionsListEl?.contains(event.target)
    ) {
      open = false;
      sliceElementOpen = false;
    }
  }

  // The review rows form their own stacking context, so the sticky pagination bar covered
  // the menu despite its z-index. Attached to <body> it always lies above the bar.
  function portal(node) {
    document.body.appendChild(node);
    return { destroy: () => node.remove() };
  }

  function closeMenu() {
    open = false;
    sliceElementOpen = false;
  }

  function handleSliceElement() {
    sliceElementOpen = true;
    open = false;
  }

  function handleOpenNumberAssistant() {
    numberAssistantRule = { prefix: displayRemoveAnonType };
    numberAssistantOpen = true;
    open = false;
  }

  $$restProps;
</script>

<svelte:window
  on:click={handleClickOutside}
  on:resize={positionMenu}
  on:scroll|capture={positionMenu}
  on:close-anonymization-menu={closeMenu}
/>

{#if firstElement}
  <div class="first-element" class:has-app-icon={appIcon && !appIconFailed}>
    <div class="app-identity">
      {#if appIcon && !appIconFailed}
        <img class="app-icon" src={appIcon} alt={anontype} title={appTitle || anontype} on:load={handleAppIconLoad} on:error={() => (appIconFailed = true)} />
      {:else}
        <span class="app-name" title={appTitle || anontype}>{anontype}</span>
      {/if}
    </div>
  </div>
{:else}
  <div
    class="anon-element"
    bind:this={elementRef}
  >
      <div
        class="read-element-wrapper"
        title={readElement}
        data-tooltip={readElement}
      >
        <span class="read-element" title={readElement}>{displayReadElement}</span>
    </div>
    <div class="custom-select-wrapper">
      <button
        type="button"
        title={readElement}
        class="custom-select"
        class:notype={!isValidSelection}
        class:active={open}
        class:multi-selected={selected}
        aria-haspopup="listbox"
        aria-expanded={open}
        on:mousedown={handleMenuMouseDown}
        on:click={handleMenuClick}
        on:keydown={handleKey}
      >
        <span class="selected-value">{displayAnonType}</span>
      </button>
      {#if open}
        <div
          class="options-list"
          style={`left: ${menuPosition.left}; top: ${menuPosition.top}; width: ${menuWidth}; max-height: ${menuPosition.maxHeight};`}
          bind:this={optionsListEl}
          use:portal
          role="listbox"
        >
          <div class="columns-wrapper" bind:this={columnsWrapperEl}>
            {#each Object.values(anontypeOptionsMap) as column}
              <div class="column">
                <span class="anon-type header">{column.title}</span>
                <div class="divider"></div>
                {#if column.options}
                  {#each column.options as option}
                    <div
                      class="option"
                      role="option"
                      aria-selected={anontype === option.value}
                      tabindex="0"
                      on:keydown={(event) => handleOptionKeydown(event, option)}
                      on:mousedown={() => selectOption(option)}
                    >
                      {option.value}
                    </div>
                  {/each}
                {/if}
              </div>
            {/each}
          </div>

          <div class="remove-wrapper">
            <span class="anon-type remove">
              {multiSelected
                ? "Selected Elements (Multiselect)"
                : "Remove Anon-Type"}
            </span>
            <div class="divider"></div>
            {#if multiSelected}
              <div class="selected-elements-wrapper">
                {#each selectedElementValues as selectedElement}
                  <span
                    class="selected-element-chip"
                    class:notype={!isValidSelectedElement(selectedElement)}
                    title={selectedElement.type}
                  >{selectedElement.type}</span>
                {/each}
              </div>
              <div class="multi-select-actions">
                <Button
                  text="Remove Anon"
                  on:click={() => {
                    dispatch("removeSelected");
                    open = false;
                  }}
                  kind="secondary"
                  size="field"
                />
              </div>
            {:else}
              <div
                class="option remove"
                role="button"
                tabindex="0"
                data-remove-option="true"
                on:keydown={(event) =>
                  handleOptionKeydown(event, { value: removeAnonType })}
                on:mousedown={() => selectOption({ value: removeAnonType })}
                title={displayRemoveAnonType}
              >
                {displayRemoveAnonType}
              </div>
            {/if}
          </div>
          {#if !multiSelected}
          <div class="remove-wrapper">
            <span class="anon-type remove">Actions</span>
            <div class="divider"></div>
            <div class="action_wrapper">
              <Button
                text={`Slice Element`}
                on:click={handleSliceElement}
                kind="secondary"
                size="field"
              />
              <Button
                text={`Number assistant`}
                on:click={handleOpenNumberAssistant}
                kind="secondary"
                size="field"
              />
            </div>
          </div>
          {/if}
        </div>
      {/if}
      <SliceElement
        bind:open={sliceElementOpen}
        {readElement}
        on:confirm={(e) => dispatch("sliced", e.detail)}
      />
    </div>
  </div>
{/if}

<NumberRuleModal
  bind:open={numberAssistantOpen}
  bind:currentRule={numberAssistantRule}
  {idproject}
/>

<style>
  .anon-element {
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: center;
    justify-content: space-evenly;
    height: 4rem;
    min-height: 4rem;
    box-sizing: border-box;
  }

  .read-element {
    color: var(--pax-black, #1c1c20);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
    /* word-wrap: break-word; */
    display: block;
    max-width: 30ch;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .read-element-wrapper {
    position: relative;
    max-width: 30ch;
  }

  .custom-select-wrapper {
    position: relative;
    display: flex;
    width: 100%;
    justify-content: center;
  }

  .custom-select {
    background: #8dcfff;
    border-radius: 0.3125rem;
    font-size: 0.75rem;
    color: #1c1c20;
    padding: 0.375rem 0.75rem;
    height: 1.75rem;
    cursor: pointer;
    outline: none;
    border: 1px solid #8dcfff;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
  }

  .notype {
    background: var(--pax-white);
  }

  .custom-select.notype {
    border-color: var(--pax-white);
  }

  .custom-select.active {
    border-color: var(--pax-black, #1c1c20);
  }

  .first-element {
    height: 43px;
    padding-top: 10px;
    padding-bottom: 10px;
    padding-left: 0;
    padding-right: 10px;
    border-right: 1px black solid;
    justify-content: center;
    align-items: center;
    gap: 0.625rem;
    display: flex;
  }

  .first-element.has-app-icon {
    padding-left: 10px;
  }

  .app-identity {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    min-width: 0;
  }

  .app-icon {
    width: 1.5rem;
    height: 1.5rem;
    object-fit: contain;
  }

  .app-name {
    max-width: 16ch;
    overflow: hidden;
    color: var(--pax-sub, #474752);
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .selected-value {
    flex: 1;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 8rem;
  }
  /* 
  .options-list {
    position: absolute;
    top: 100%;
    left: 0;
    width: 6rem;
    background: #fff;
    border-radius: 0.3125rem;
    box-shadow: 0 0.125rem 0.5rem rgba(44, 44, 44, 0.15);
    z-index: 10;
    margin-top: 0.25rem;
    padding: 0.25rem 0;
  } */
  .options-list {
    position: fixed;
    width: auto; /* Changed from fixed 6rem to auto */
    background: #fff;
    border-radius: 0.3125rem;
    box-shadow: 0 0.125rem 0.5rem rgba(44, 44, 44, 0.15);
    z-index: 100000;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: calc(100vh - 1rem);
    max-width: calc(100vw - 1rem);
    box-sizing: border-box;
    overflow-y: auto;
  }

  .columns-wrapper {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-self: flex-start;
  }

  .column {
    display: flex;
    flex-direction: column;
    width: 6rem;
  }

  .remove-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
  }
  .anon-type {
    font-size: 0.5rem;
    font-style: normal;
    font-weight: 400;
    line-height: 100%; /* 0.5rem */
    letter-spacing: -0.0055rem;
    padding: 0 0.44rem;
  }

  .divider {
    height: 0;
    border-bottom: 1px solid var(--pax-black, #1c1c20);
    margin: 0.44rem;
  }

  .option {
    display: inline-block;
    background: #8dcfff;
    padding: 0.31rem;
    margin: 0.22rem 0.44rem;
    border-radius: 0.3125rem;
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: -0.00825rem;
    color: #1c1c20;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 0.88rem);
  }

  .option.remove {
    background: #f2f2f2;
    cursor: default;
    width: auto;
    max-width: calc(100% - 0.88rem);
    box-sizing: border-box;
  }

  .selected-elements-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0 0.44rem;
    min-width: 0;
  }

  .multi-select-actions {
    display: flex;
    justify-content: flex-start;
    margin-top: 0.5rem;
    padding: 0 0.44rem;
  }

  .selected-element-chip {
    display: block;
    width: fit-content;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    padding: 0.31rem 0.44rem;
    border: 1px solid var(--pax-black, #1c1c20);
    border-radius: 0.3125rem;
    background: #8dcfff;
    color: #1c1c20;
    font-size: 0.75rem;
    line-height: 150%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .selected-element-chip.notype {
    background: var(--pax-white);
  }

  .action_wrapper {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    justify-content: space-between;
    padding: 0 0.44rem;
  }

  .action_wrapper :global(button) {
    flex: 1 1 0;
    width: 100% !important;
  }

  .custom-select.multi-selected {
    border-color: var(--pax-black, #1c1c20) !important;
  }
</style>
